import { convertToLocal } from 'helper/utility'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import Calendar from 'react-calendar'

interface SelectDatesCalendarProps {
  isLimited: boolean
  isRange?: boolean
  startDate?: string
  endDate?: string
  selectedDates: string[]
  setSelectedDates: React.Dispatch<React.SetStateAction<string[]>>
  occurrenceDates?: string[]
  showSelectDatesButtons?: boolean
  /** Optional: notifies the parent of the currently-displayed month.
   *  Additive only — existing consumers are unaffected. */
  onActiveMonthChange?: (date: Date) => void
}

const SelectDatesCalendar: React.FC<SelectDatesCalendarProps> = ({
  isLimited,
  isRange,
  startDate,
  endDate,
  selectedDates,
  setSelectedDates,
  occurrenceDates,
  showSelectDatesButtons,
  onActiveMonthChange,
}) => {
  const [availableDates, setAvailableDates] = useState<Date[]>([])
  const [activeStartDate, setActiveStartDate] = useState<Date>(new Date())
  // Ensures we only auto-focus the selected month once (on edit load), so it
  // never yanks the view back after the user manually navigates.
  const didAutoFocusSelected = useRef(false)

  const handleTileClick = (date: Date) => {
    const formattedDate = moment(date).format('YYYY-MM-DD')
    if (!selectedDates?.includes(formattedDate)) {
      if (selectedDates) {
        setSelectedDates([...selectedDates, formattedDate])
      } else {
        setSelectedDates([formattedDate])
      }
    } else {
      setSelectedDates(
        selectedDates.filter(selectedDate => selectedDate !== formattedDate),
      )
    }
  }

  useEffect(() => {
    if (occurrenceDates) {
      const localFormatedDate: Date[] = []
      occurrenceDates.forEach(date => {
        const utcDate = new Date(date)
        utcDate.setMinutes(utcDate.getMinutes() + utcDate.getTimezoneOffset())
        localFormatedDate.push(utcDate)
      })
      setAvailableDates(localFormatedDate)
    }

    if (isRange) {
      const localFormattedDate: Date[] = []
      const start = new Date(startDate as string)
      const end = new Date(endDate as string)
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const LocalDate = new Date(convertToLocal(d.toISOString()))
        localFormattedDate.push(LocalDate)
      }
      setAvailableDates(localFormattedDate)
    }
  }, [occurrenceDates, isRange, startDate, endDate])

  useEffect(() => {
    if (isLimited && startDate) {
      const start = moment(convertToLocal(startDate), 'YYYY-MM-DD')
        .startOf('month')
        .toDate()
      setActiveStartDate(start)
    }
  }, [isLimited, startDate])

  // On first load with pre-selected dates (edit flow), open the calendar on the
  // month of the EARLIEST selected date instead of the current month — otherwise
  // dates saved in a future month aren't visible until the user navigates there.
  // Runs once (guarded by the ref) so manual navigation is never overridden.
  useEffect(() => {
    if (didAutoFocusSelected.current) return
    if (selectedDates && selectedDates.length > 0) {
      const earliest = [...selectedDates]
        .map(d => moment(d, 'YYYY-MM-DD'))
        .filter(m => m.isValid())
        .sort((a, b) => a.valueOf() - b.valueOf())[0]
      if (earliest) {
        setActiveStartDate(earliest.startOf('month').toDate())
        didAutoFocusSelected.current = true
      }
    }
  }, [selectedDates])

  const isDateSelectable = (date: Date) => {
    if (!isLimited) return true
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return availableDates.some(availableDate => {
      const availableDateNormalized = new Date(availableDate)
      availableDateNormalized.setHours(0, 0, 0, 0)
      return (
        availableDateNormalized.getTime() >= today.getTime() &&
        availableDate.getDate() === date.getDate() &&
        availableDate.getMonth() === date.getMonth()
      )
    })
  }

  const getSelectableDatesInMonth = (monthDate: Date, minDateObj?: Date) => {
    const year = monthDate.getFullYear()
    const month = monthDate.getMonth()
    const first = new Date(year, month, 1)
    const last = new Date(year, month + 1, 0)
    const dates: string[] = []
    // determine minimum allowed date (start of limited range or today)
    const minObj = minDateObj
      ? new Date(minDateObj)
      : isLimited
      ? moment(convertToLocal(startDate), 'YYYY-MM-DD').toDate()
      : new Date()
    // compare at day granularity — candidate days are at midnight, so a mid-day
    // "now" would wrongly exclude today
    minObj.setHours(0, 0, 0, 0)

    for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
      const copy = new Date(d)
      if (isDateSelectable(copy) && copy.getTime() >= minObj.getTime()) {
        dates.push(moment(copy).format('YYYY-MM-DD'))
      }
    }
    return dates
  }

  const handleSelectAllDays = () => {
    const month = activeStartDate || new Date()
    const minDateObj = isLimited
      ? moment(convertToLocal(startDate), 'YYYY-MM-DD').toDate()
      : new Date()

    const monthDates = getSelectableDatesInMonth(month, minDateObj)
    if (monthDates.length === 0) return

    const allSelected = monthDates.every(d => selectedDates?.includes(d))
    if (allSelected) {
      setSelectedDates(selectedDates.filter(sd => !monthDates.includes(sd)))
    } else {
      setSelectedDates(
        Array.from(new Set([...(selectedDates || []), ...monthDates])),
      )
    }
  }

  const handleSelectWeekdays = () => {
    const month = activeStartDate || new Date()
    const year = month.getFullYear()
    const monthIndex = month.getMonth()
    const first = new Date(year, monthIndex, 1)
    const last = new Date(year, monthIndex + 1, 0)
    const weekdayDates: string[] = []
    const weekendDates: string[] = []
    const minDateObj = isLimited
      ? moment(convertToLocal(startDate), 'YYYY-MM-DD').toDate()
      : new Date()
    // compare at day granularity so today isn't excluded by the current time-of-day
    minDateObj.setHours(0, 0, 0, 0)

    for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
      const copy = new Date(d)
      const day = copy.getDay()
      const isWeekday = day !== 0 && day !== 6
      const formattedDate = moment(copy).format('YYYY-MM-DD')

      // Only include dates that are selectable and not in the past
      if (isDateSelectable(copy) && copy.getTime() >= minDateObj.getTime()) {
        if (isWeekday) {
          weekdayDates.push(formattedDate)
        } else {
          weekendDates.push(formattedDate)
        }
      }
    }

    if (weekdayDates.length === 0) return

    const allWeekdaysSelected = weekdayDates.every(
      d => selectedDates?.includes(d),
    )

    if (allWeekdaysSelected) {
      // Remove weekdays, keep everything else
      setSelectedDates(selectedDates.filter(sd => !weekdayDates.includes(sd)))
    } else {
      // Remove weekends from this month and add all weekdays
      const filteredDates = selectedDates.filter(
        sd => !weekendDates.includes(sd),
      )
      setSelectedDates(Array.from(new Set([...filteredDates, ...weekdayDates])))
    }
  }

  return (
    <>
      {isLimited ? (
        <Calendar
          calendarType="gregory"
          minDate={moment(convertToLocal(startDate), 'YYYY-MM-DD').toDate()}
          onClickDay={handleTileClick}
          onActiveStartDateChange={({ activeStartDate }) => {
            setActiveStartDate(activeStartDate)
            onActiveMonthChange?.(activeStartDate)
          }}
          tileClassName={({ date, view }) => {
            if (
              selectedDates?.find((x: any) => {
                return x === moment(date).format('YYYY-MM-DD')
              })
            ) {
              return 'selected'
            }
          }}
          maxDate={moment(convertToLocal(endDate), 'YYYY-MM-DD').toDate()}
          // tileDisabled={({ date }) =>
          //   !availableDates.some(
          //     availableDate =>
          //       availableDate.getDate() === date.getDate() &&
          //       availableDate.getMonth() === date.getMonth(),
          //   )
          // }
          tileDisabled={({ date }) => {
            const newDate = new Date()
            return !availableDates.some(
              availableDate =>
                availableDate.getTime() > newDate.getTime() &&
                availableDate.getDate() === date.getDate() &&
                availableDate.getMonth() === date.getMonth(),
            )
          }}
        />
      ) : (
        <Calendar
          calendarType="gregory"
          minDate={new Date()}
          activeStartDate={activeStartDate}
          onClickDay={handleTileClick}
          onActiveStartDateChange={({ activeStartDate }) => {
            setActiveStartDate(activeStartDate)
            onActiveMonthChange?.(activeStartDate)
          }}
          tileClassName={({ date, view }) => {
            if (
              selectedDates?.find(
                (x: any) => x === moment(date).format('YYYY-MM-DD'),
              )
            ) {
              return 'selected'
            }
          }}
        />
      )}

      {showSelectDatesButtons && (
        <div className="mt-15px w-full flex justify-center gap-10px">
          <button className="btn-secondary" onClick={handleSelectAllDays}>
            All days of month
          </button>
          <button className="btn-secondary" onClick={handleSelectWeekdays}>
            All weekdays of month
          </button>
        </div>
      )}
    </>
  )
}

export default SelectDatesCalendar
